import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import Header from './Header';
import { apiService } from '../services/api';
import { rowMatchesSmartSearch } from '../utils/fdaSearch';
import { conferenceUrlLabel, extractConferenceUrl } from '../utils/conferenceUrls';
import { cleanConferenceText } from '../utils/conferenceText';

export interface ConferenceAbstractRow {
  id?: number;
  conference_presentation_title?: string;
  abstract_number?: string;
  poster_board_number?: string;
  abstract_url?: string;
  abstract_text?: string;
  presentation_type?: string;
  presentation_date?: string;
  presentation_time?: string;
  location?: string;
  therapeutic_area?: string;
  company?: string;
  materials?: string;
  drug?: string;
  target?: string;
  moa?: string;
  phase?: string;
  trial_name?: string;
  highlights?: string;
  impact?: string;
  ruby_preferred?: boolean;
  image_url?: string;
  link_to_posters_presentations?: string;
  conference?: string;
  meeting_year?: string | number;
  asco_uid?: string;
  presentation_id?: string;
  primary_presenter?: string;
  scraped_at?: string;
  updated_at?: string;
}

/** UI column → DB column mapping (conference_presentations has no impact/modality columns). */
const FIELD_MAP = {
  abstractId: ['abstract_number', 'presentation_id', 'asco_uid'] as const,
  presentationTitle: ['conference_presentation_title'] as const,
  asset: ['drug'] as const,
  modality: ['moa'] as const,
  target: ['target'] as const,
  company: ['company'] as const,
  impact: ['impact'] as const,
  abstractUrl: ['abstract_url'] as const,
  summarizedAbstract: ['abstract_text', 'materials'] as const,
  commentary: ['highlights'] as const,
};

interface MeetingTag {
  id: string;
  label: string;
  conference: string;
  year: string;
}

function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  const s = String(value).trim();
  return Boolean(s) && s.toLowerCase() !== 'n/a' && s !== '—' && s !== '-';
}

function normalizeYear(value: unknown): string {
  const text = (value ?? '').toString().trim();
  if (!text) return '';
  const match = text.match(/(20\d{2})/);
  return match ? match[1] : text;
}

/** Strip trailing meeting years from conference titles (e.g. "ESMO GI 2026" → "ESMO GI"). */
function normalizeConferenceName(value: unknown): string {
  let name = (value ?? '').toString().trim();
  if (!name) return '';
  // Remove one or more trailing years: "ESMO GI 2026", "ASCO 2025 2025"
  while (/\s+20\d{2}$/.test(name)) {
    name = name.replace(/\s+20\d{2}$/, '').trim();
  }
  return name;
}

function rowMatchesConferenceSelection(
  row: ConferenceAbstractRow,
  years: string[],
  conferences: string[],
): boolean {
  // Empty year/conference selection = no restriction (show all).
  const rowYear = normalizeYear(row.meeting_year);
  const rowConf = normalizeConferenceName(row.conference).toLowerCase();
  const yearOk =
    years.length === 0 || years.some((y) => normalizeYear(y) === rowYear);
  const confOk =
    conferences.length === 0 ||
    conferences.some((c) => normalizeConferenceName(c).toLowerCase() === rowConf);
  return yearOk && confOk;
}

function conferenceSearchFields(row: ConferenceAbstractRow): (string | undefined)[] {
  return [
    row.abstract_number,
    row.presentation_id,
    row.asco_uid,
    row.drug,
    row.moa,
    row.target,
    row.company,
    row.impact,
    row.presentation_type,
    row.abstract_text,
    row.highlights,
    row.conference_presentation_title,
    row.primary_presenter,
    row.trial_name,
    row.therapeutic_area,
    row.phase,
    row.conference,
    row.meeting_year != null ? String(row.meeting_year) : undefined,
  ];
}

function firstValue(row: ConferenceAbstractRow, keys: readonly (keyof ConferenceAbstractRow)[]): string {
  for (const key of keys) {
    const v = row[key];
    if (hasValue(v)) return String(v);
  }
  return '—';
}

function abstractId(row: ConferenceAbstractRow): string {
  return firstValue(row, FIELD_MAP.abstractId);
}

function presentationTitle(row: ConferenceAbstractRow): string {
  const raw = firstValue(row, FIELD_MAP.presentationTitle);
  if (raw === '—') return raw;
  return cleanConferenceText(raw) || raw;
}

function assetLabel(row: ConferenceAbstractRow): string {
  return firstValue(row, FIELD_MAP.asset);
}

function modalityLabel(row: ConferenceAbstractRow): string {
  return firstValue(row, FIELD_MAP.modality);
}

/** Conference name only — year lives in the Year filter / meeting_year. */
function rowMeetingLabel(row: ConferenceAbstractRow): string {
  const conf = normalizeConferenceName(row.conference);
  return conf || '—';
}

function parseMultiValueCell(value: unknown, field: 'modality' | 'target' | 'company'): string[] {
  if (!hasValue(value)) return [];
  const separatorPattern = field === 'company' ? /[,;]/ : /[,;/]/;
  return String(value)
    .split(separatorPattern)
    .map((part) => part.trim())
    .filter((part) => hasValue(part));
}

function normalizeFilterKey(token: string, field: 'modality' | 'target' | 'company'): string {
  let key = token.trim().toLowerCase().replace(/\s+/g, ' ');
  if (field === 'company') {
    key = key
      .replace(/[.,']/g, '')
      .replace(/\band company\b/g, '')
      .replace(/\b(incorporated|inc|corp|corporation|ltd|llc|plc|gmbh)\b\.?/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  if (field === 'target') {
    key = key
      .replace(/\s*\/\s*/g, '/')
      .replace(/\s*,\s*/g, ',')
      .replace(/\s*-\s*/g, '-');
  }
  return key;
}

function pickDisplayLabel(
  existing: string,
  candidate: string,
  field: 'modality' | 'target' | 'company',
): string {
  if (field === 'company') {
    return candidate.length < existing.length ? candidate : existing;
  }
  if (existing.length === candidate.length) {
    return existing.localeCompare(candidate) <= 0 ? existing : candidate;
  }
  return candidate.length > existing.length ? candidate : existing;
}

function columnTokens(row: ConferenceAbstractRow, field: 'modality' | 'target' | 'company'): string[] {
  if (field === 'modality') return parseMultiValueCell(modalityLabel(row), 'modality');
  if (field === 'target') return parseMultiValueCell(row.target, 'target');
  return parseMultiValueCell(row.company, 'company');
}

function rowMatchesColumnFilter(row: ConferenceAbstractRow, field: 'modality' | 'target' | 'company', filter: string): boolean {
  if (filter === 'All') return true;
  const wantKey = normalizeFilterKey(filter, field);
  return columnTokens(row, field).some((token) => normalizeFilterKey(token, field) === wantKey);
}

function distinctColumnValues(
  rows: ConferenceAbstractRow[],
  field: 'modality' | 'target' | 'company',
): string[] {
  const byKey = new Map<string, string>();
  for (const row of rows) {
    for (const token of columnTokens(row, field)) {
      const key = normalizeFilterKey(token, field);
      if (!key) continue;
      const existing = byKey.get(key);
      byKey.set(key, existing ? pickDisplayLabel(existing, token, field) : token);
    }
  }
  return [...byKey.values()].sort((a, b) => a.localeCompare(b));
}

function isFilterValueInOptions(
  filter: string,
  options: string[],
  field: 'modality' | 'target' | 'company',
): boolean {
  if (filter === 'All') return true;
  const wantKey = normalizeFilterKey(filter, field);
  return options.some((opt) => normalizeFilterKey(opt, field) === wantKey);
}

function distinctYears(rows: ConferenceAbstractRow[], tags: MeetingTag[]): string[] {
  const years = new Set<string>();
  for (const tag of tags) {
    const year = normalizeYear(tag.year);
    if (year) years.add(year);
  }
  for (const row of rows) {
    const year = normalizeYear(row.meeting_year);
    if (year) years.add(year);
  }
  return [...years].sort((a, b) => b.localeCompare(a));
}

function distinctConferencesForYears(rows: ConferenceAbstractRow[], years: string[]): string[] {
  const yearSet = new Set(years.map((y) => normalizeYear(y)).filter(Boolean));
  const byLower = new Map<string, string>();
  for (const row of rows) {
    if (yearSet.size > 0 && !yearSet.has(normalizeYear(row.meeting_year))) continue;
    const name = normalizeConferenceName(row.conference);
    if (!name) continue;
    const key = name.toLowerCase();
    if (!byLower.has(key)) byLower.set(key, name);
  }
  return [...byLower.values()].sort((a, b) => a.localeCompare(b));
}

function formatMeetingSelectionLabel(years: string[], conferences: string[]): string {
  if (years.length === 0 && conferences.length === 0) return 'All meetings';
  const confPart = conferences.length > 0 ? conferences.join(', ') : 'All conferences';
  const yearPart = years.length > 0 ? years.join(', ') : 'All years';
  return `${confPart} · ${yearPart}`;
}

function toggleInList(list: string[], value: string, { sortDesc = false } = {}): string[] {
  const key = value.toLowerCase();
  const exists = list.some((item) => item.toLowerCase() === key);
  if (exists) {
    return list.filter((item) => item.toLowerCase() !== key);
  }
  const next = [...list, value];
  return sortDesc
    ? next.sort((a, b) => b.localeCompare(a))
    : next.sort((a, b) => a.localeCompare(b));
}

const ConferenceAbstractsView: React.FC = () => {
  const [rows, setRows] = useState<ConferenceAbstractRow[]>([]);
  const [meetingTags, setMeetingTags] = useState<MeetingTag[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [selectedConferences, setSelectedConferences] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalityFilter, setModalityFilter] = useState('All');
  const [targetFilter, setTargetFilter] = useState('All');
  const [companyFilter, setCompanyFilter] = useState('All');
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.fetchConferenceAbstracts();
        if (response?.success) {
          const data = response.data || [];
          const tags = response.meeting_tags || [];
          setRows(data);
          setMeetingTags(tags);
          // Default: show all abstracts (no year/conference restriction).
          setSelectedYears([]);
          setSelectedConferences([]);
        } else {
          setError(response?.error || 'Failed to load conference abstracts');
        }
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
            : err instanceof Error
              ? err.message
              : 'Failed to load conference abstracts';
        setError(message || 'Failed to load conference abstracts');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const yearOptions = useMemo(() => distinctYears(rows, meetingTags), [rows, meetingTags]);

  const conferenceOptions = useMemo(
    () => distinctConferencesForYears(rows, selectedYears),
    [rows, selectedYears],
  );

  // When years change, drop conference picks that no longer exist in the year scope.
  useEffect(() => {
    if (selectedConferences.length === 0) return;
    setSelectedConferences((prev) => {
      const stillValid = prev.filter((name) =>
        conferenceOptions.some((opt) => opt.toLowerCase() === name.toLowerCase()),
      );
      return stillValid.length === prev.length ? prev : stillValid;
    });
  }, [conferenceOptions, selectedConferences.length]);

  const conferenceRows = useMemo(
    () => rows.filter((row) => rowMatchesConferenceSelection(row, selectedYears, selectedConferences)),
    [rows, selectedYears, selectedConferences],
  );

  const meetingLabel = formatMeetingSelectionLabel(selectedYears, selectedConferences);
  const hasMeetingFilters = selectedYears.length > 0 || selectedConferences.length > 0;

  useEffect(() => {
    setModalityFilter('All');
    setTargetFilter('All');
    setCompanyFilter('All');
  }, [selectedYears, selectedConferences]);

  const toggleYear = (year: string) => {
    setSelectedYears((prev) => toggleInList(prev, year, { sortDesc: true }));
  };

  const toggleConference = (name: string) => {
    setSelectedConferences((prev) => toggleInList(prev, name));
  };

  const clearMeetingFilters = () => {
    setSelectedYears([]);
    setSelectedConferences([]);
  };

  const rowsForModalityOptions = useMemo(() => {
    if (targetFilter === 'All') return conferenceRows;
    return conferenceRows.filter((row) => rowMatchesColumnFilter(row, 'target', targetFilter));
  }, [conferenceRows, targetFilter]);

  const rowsForTargetOptions = useMemo(() => {
    if (modalityFilter === 'All') return conferenceRows;
    return conferenceRows.filter((row) => rowMatchesColumnFilter(row, 'modality', modalityFilter));
  }, [conferenceRows, modalityFilter]);

  const rowsForCompanyOptions = useMemo(() => {
    let list = conferenceRows;
    if (modalityFilter !== 'All') {
      list = list.filter((row) => rowMatchesColumnFilter(row, 'modality', modalityFilter));
    }
    if (targetFilter !== 'All') {
      list = list.filter((row) => rowMatchesColumnFilter(row, 'target', targetFilter));
    }
    return list;
  }, [conferenceRows, modalityFilter, targetFilter]);

  const modalityOptions = useMemo(
    () => distinctColumnValues(rowsForModalityOptions, 'modality'),
    [rowsForModalityOptions],
  );
  const targetOptions = useMemo(
    () => distinctColumnValues(rowsForTargetOptions, 'target'),
    [rowsForTargetOptions],
  );
  const companyOptions = useMemo(
    () => distinctColumnValues(rowsForCompanyOptions, 'company'),
    [rowsForCompanyOptions],
  );

  useEffect(() => {
    if (!isFilterValueInOptions(modalityFilter, modalityOptions, 'modality')) {
      setModalityFilter('All');
    }
  }, [modalityFilter, modalityOptions]);

  useEffect(() => {
    if (!isFilterValueInOptions(targetFilter, targetOptions, 'target')) {
      setTargetFilter('All');
    }
  }, [targetFilter, targetOptions]);

  useEffect(() => {
    if (!isFilterValueInOptions(companyFilter, companyOptions, 'company')) {
      setCompanyFilter('All');
    }
  }, [companyFilter, companyOptions]);

  const filteredRows = useMemo(() => {
    let list = conferenceRows;
    if (modalityFilter !== 'All') {
      list = list.filter((row) => rowMatchesColumnFilter(row, 'modality', modalityFilter));
    }
    if (targetFilter !== 'All') {
      list = list.filter((row) => rowMatchesColumnFilter(row, 'target', targetFilter));
    }
    if (companyFilter !== 'All') {
      list = list.filter((row) => rowMatchesColumnFilter(row, 'company', companyFilter));
    }
    if (searchQuery.trim()) {
      list = list.filter((row) => rowMatchesSmartSearch(searchQuery, conferenceSearchFields(row)));
    }
    return list;
  }, [conferenceRows, modalityFilter, targetFilter, companyFilter, searchQuery]);

  const hasActiveSubFilters =
    hasMeetingFilters ||
    modalityFilter !== 'All' ||
    targetFilter !== 'All' ||
    companyFilter !== 'All' ||
    Boolean(searchQuery.trim());

  const clearSubFilters = () => {
    clearMeetingFilters();
    setModalityFilter('All');
    setTargetFilter('All');
    setCompanyFilter('All');
    setSearchQuery('');
  };

  const renderLink = (url?: string, label = 'Open link') => {
    const href = extractConferenceUrl(url);
    if (!href) return <span className="text-slate-400">—</span>;
    const linkLabel = label === 'Open link' ? conferenceUrlLabel(url, label) : label;
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-indigo-600 hover:text-indigo-700 font-medium break-all underline underline-offset-2"
        onClick={(e) => e.stopPropagation()}
      >
        {linkLabel}
      </a>
    );
  };

  const renderTextBlock = (value?: string) => {
    const cleaned = cleanConferenceText(value);
    if (!hasValue(cleaned)) return <span className="text-slate-400">—</span>;
    return <p className="whitespace-pre-wrap break-words text-slate-700 leading-relaxed">{cleaned}</p>;
  };

  const toggleRow = (rowKey: string) => {
    setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  const showConferenceColumn = selectedConferences.length !== 1;
  const tableColSpan = showConferenceColumn ? 8 : 7;

  return (
    <div className="h-screen flex flex-col text-slate-900 bg-slate-50 font-sans overflow-hidden">
      <Header />

      <main className="w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6 py-4 flex flex-col flex-1 min-h-0">
        <div className="flex-shrink-0 bg-white rounded-xl shadow-card border border-gray-100 px-4 py-3 mb-3">
          <h1 className="text-[21px] font-bold text-slate-900">Conference Abstracts</h1>
          <p className="text-slate-500 text-[13px] mt-0.5">
            Ruby Preferred abstracts only — all meetings by default. Select years and conferences to
            narrow; expand a row for links and context.
          </p>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center bg-white rounded-xl shadow-card border border-gray-100">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center bg-white rounded-xl shadow-card border border-gray-100 p-8">
            <p className="text-center text-rose-600 font-medium">{error}</p>
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0 bg-white rounded-xl shadow-card border border-gray-100 overflow-hidden">
            <div className="flex-shrink-0 px-4 py-2.5 border-b border-gray-100 space-y-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-[17px] font-bold text-slate-900">Abstracts</h2>
                  <span className="text-[13px] text-slate-500">
                    {filteredRows.length} of {conferenceRows.length} abstract
                    {conferenceRows.length === 1 ? '' : 's'}
                    {meetingLabel ? ` · ${meetingLabel}` : ''}
                  </span>
                </div>
                {hasActiveSubFilters && (
                  <button
                    type="button"
                    onClick={clearSubFilters}
                    className="text-[13px] font-semibold text-indigo-600 hover:text-indigo-800 self-start sm:self-auto"
                  >
                    Clear filters
                  </button>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-slate-600">
                  <div className="inline-flex flex-wrap items-center gap-1">
                    <span className="font-semibold text-slate-500 text-[11px] uppercase tracking-wide mr-0.5">
                      Year
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedYears([])}
                      className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
                        selectedYears.length === 0
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                      }`}
                    >
                      All
                    </button>
                    {yearOptions.map((year) => {
                      const active = selectedYears.some((y) => y === year);
                      return (
                        <button
                          key={year}
                          type="button"
                          onClick={() => toggleYear(year)}
                          className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
                            active
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                          }`}
                        >
                          {year}
                        </button>
                      );
                    })}
                  </div>
                  <div className="inline-flex flex-wrap items-center gap-1">
                    <span className="font-semibold text-slate-500 text-[11px] uppercase tracking-wide mr-0.5">
                      Conference
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedConferences([])}
                      className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
                        selectedConferences.length === 0
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                      }`}
                    >
                      All
                    </button>
                    {conferenceOptions.length === 0 ? (
                      <span className="text-[11px] text-slate-400 italic">None</span>
                    ) : (
                      conferenceOptions.map((name) => {
                        const active = selectedConferences.some(
                          (c) => c.toLowerCase() === name.toLowerCase(),
                        );
                        return (
                          <button
                            key={name}
                            type="button"
                            onClick={() => toggleConference(name)}
                            className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
                              active
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                            }`}
                          >
                            {name}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                {rows.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 sm:flex sm:flex-wrap sm:items-center">
                    <label className="flex items-center gap-1.5 text-[12px] text-slate-600 min-w-0">
                      <span className="font-semibold text-slate-500 shrink-0">Modality</span>
                      <select
                        value={modalityFilter}
                        onChange={(e) => setModalityFilter(e.target.value)}
                        className="rounded-md border border-slate-200 px-1.5 py-0.5 text-[12px] min-w-0 flex-1 sm:max-w-[140px]"
                      >
                        <option value="All">All</option>
                        {modalityOptions.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center gap-1.5 text-[12px] text-slate-600 min-w-0">
                      <span className="font-semibold text-slate-500 shrink-0">Target</span>
                      <select
                        value={targetFilter}
                        onChange={(e) => setTargetFilter(e.target.value)}
                        className="rounded-md border border-slate-200 px-1.5 py-0.5 text-[12px] min-w-0 flex-1 sm:max-w-[140px]"
                      >
                        <option value="All">All</option>
                        {targetOptions.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center gap-1.5 text-[12px] text-slate-600 min-w-0">
                      <span className="font-semibold text-slate-500 shrink-0">Company</span>
                      <select
                        value={companyFilter}
                        onChange={(e) => setCompanyFilter(e.target.value)}
                        className="rounded-md border border-slate-200 px-1.5 py-0.5 text-[12px] min-w-0 flex-1 sm:max-w-[160px]"
                      >
                        <option value="All">All</option>
                        {companyOptions.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </label>
                    <input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search…"
                      className="col-span-2 sm:col-span-1 rounded-md border border-slate-200 px-2 py-0.5 text-[12px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 w-full sm:w-44 sm:ml-auto"
                    />
                  </div>
                )}
              </div>

            </div>

            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full text-[12px] border-collapse min-w-[780px]">
                <thead className="bg-slate-50 border-b border-gray-100 sticky top-0 z-10">
                  <tr>
                    <th className="w-8 px-1 py-2" aria-label="Expand" />
                    {showConferenceColumn && (
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase whitespace-nowrap min-w-[100px]">
                        Conference
                      </th>
                    )}
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase whitespace-nowrap">
                      Abstract ID
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase min-w-[220px]">
                      Title
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase min-w-[100px]">
                      Asset
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase min-w-[100px]">
                      Modality
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase min-w-[100px]">
                      Target
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-slate-600 uppercase min-w-[120px]">
                      Company
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={tableColSpan} className="px-4 py-8 text-center text-slate-500 italic">
                        {conferenceRows.length === 0
                          ? 'No Ruby Preferred abstracts for this selection. Clear year/conference filters or pick different ones.'
                          : 'No abstracts match the current filters.'}
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row, idx) => {
                      const rowKey = String(row.id ?? row.presentation_id ?? idx);
                      const expanded = Boolean(expandedRows[rowKey]);
                      return (
                        <React.Fragment key={rowKey}>
                          <tr
                            className={`hover:bg-slate-50 cursor-pointer ${expanded ? 'bg-indigo-50/40' : ''}`}
                            onClick={() => toggleRow(rowKey)}
                          >
                            <td className="px-1 py-2 text-slate-400 align-top">
                              {expanded ? (
                                <ChevronDown className="w-4 h-4" aria-hidden />
                              ) : (
                                <ChevronRight className="w-4 h-4" aria-hidden />
                              )}
                            </td>
                            {showConferenceColumn && (
                              <td className="px-2 py-2 text-slate-700 whitespace-nowrap align-top font-semibold">
                                {rowMeetingLabel(row)}
                              </td>
                            )}
                            <td className="px-2 py-2 text-slate-700 font-mono whitespace-nowrap align-top">
                              {abstractId(row)}
                            </td>
                            <td
                              className="px-2 py-2 font-semibold text-slate-900 whitespace-normal break-words align-top leading-snug"
                              title={presentationTitle(row)}
                            >
                              {presentationTitle(row)}
                            </td>
                            <td className="px-2 py-2 text-slate-700 whitespace-normal break-words align-top">
                              {assetLabel(row)}
                            </td>
                            <td className="px-2 py-2 text-slate-700 whitespace-normal break-words align-top">
                              {modalityLabel(row)}
                            </td>
                            <td className="px-2 py-2 text-slate-700 whitespace-normal break-words align-top">
                              {row.target || '—'}
                            </td>
                            <td className="px-2 py-2 text-slate-700 whitespace-normal break-words align-top">
                              {row.company || '—'}
                            </td>
                          </tr>
                          {expanded && (
                            <tr className="bg-slate-50/80">
                              <td colSpan={tableColSpan} className="px-4 py-3 align-top border-t border-indigo-100/60" onClick={(e) => e.stopPropagation()}>
                                <div className="grid grid-cols-1 gap-4">
                                  <div>
                                    <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                                      URL to Abstract
                                    </div>
                                    {renderLink(row.abstract_url, 'View abstract')}
                                  </div>
                                  <div>
                                    <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                                      Summarized Abstract
                                    </div>
                                    {renderTextBlock(row.abstract_text || row.materials)}
                                  </div>
                                  {hasValue(cleanConferenceText(row.highlights)) && (
                                    <div>
                                      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                                        Commentary / Benchmarked Context
                                      </div>
                                      {renderTextBlock(row.highlights)}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ConferenceAbstractsView;
